import { VenueDetailsBySlugResponse, useI18N } from "common/hooks";
import { useState } from "react";
import { Search, X } from "lucide-react";
import AnalyticsService from "@/services/analytics";
import { CaretDown, FolderDotted } from "phosphor-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";

type AccessibilityReviewsTabProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  pageName?: string; // For analytics tracking
};

type ReviewTabType = "all" | "positive" | "negative" | "neutral";

function AccessibilityReviewsTab({
  venueDetailsResponse,
  pageName = "Reviews Tab",
}: AccessibilityReviewsTabProps) {
  const i18n = useI18N();
  const [activeTab, setActiveTab] = useState<ReviewTabType>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Determine event names based on pageName
  const getEventName = (baseName: string) => {
    if (pageName === "Accessibility Space Guide") {
      const eventMap: Record<string, string> = {
        "reviewsSortChanged": "accessibilitySpaceGuideReviewsSortChanged",
      };
      return eventMap[baseName] || baseName;
    }
    return baseName;
  };
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "rating">("newest");

  const tabs = [
    { id: "all" as ReviewTabType, label: `${i18n.get("all")} (0)` },
    // { id: "positive" as ReviewTabType, label: `${i18n.get("positive")} (0)` },
    // { id: "negative" as ReviewTabType, label: `${i18n.get("negative")} (0)` },
    // { id: "neutral" as ReviewTabType, label: `${i18n.get("neutral")} (0)` },
  ];

  return (
    <div className="h-screen flex flex-col">
      {/* Secondary Filter Tabs */}
      <nav
        className="bg-white border-b border-border-token-neutral-minimal px-4 py-4 md:px-6 lg:px-8 flex-shrink-0"
        aria-label="Review filters"
      >
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
          <ul className="flex gap-4 md:gap-6 whitespace-nowrap overflow-x-auto">
            {tabs.map((tab) => (
              <li key={tab.id}>
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 rounded-md focus-blue ${
                    activeTab === tab.id
                      ? "label-m-medium text-fg-brand-strong bg-container-brand-minimal"
                      : "label-m-medium text-fg-neutral-subtle hover:bg-container-neutral-minimal"
                  }`}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Search Bar and Sort Dropdown */}
          <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:ml-auto w-full md:w-auto">
            <div className="relative w-full md:w-64">
              <label htmlFor="reviews-search" className="sr-only">
                {i18n.get("searchReviews")}
              </label>
              <input
                id="reviews-search"
                type="text"
                placeholder={i18n.get("searchReviews")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full  h-12  px-4 py-2 pr-10 rounded-lg border border-border-token-neutral-minimal title-s-semibold text-fg-neutral-strong bg-bg-brand-secondary-default placeholder:text-fg-neutral-minimal focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-fg-neutral-minimal hover:text-fg-neutral-subtle focus-blue"
                >
                  <X size={20} aria-hidden="true" />
                </button>
              ) : (
                <Search
                  size={20}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-neutral-minimal"
                  aria-hidden="true"
                />
              )}
            </div>

            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="focus:outline-none focus-visible:outline-none">
                <div className="flex items-center gap-2 px-3 py-2 label-m-medium text-fg-neutral-subtle hover:bg-container-neutral-minimal rounded-md cursor-pointer focus-blue border border-border-token-neutral-minimal">
                  <span>
                    {sortBy === "newest" && i18n.get("newest")}
                    {sortBy === "oldest" && i18n.get("oldest")}
                    {sortBy === "rating" && i18n.get("rating")}
                  </span>
                  <CaretDown size={16} />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel aria-label={i18n.get("sortBy")}>
                  {i18n.get("sortBy")}:
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuRadioGroup
                  value={sortBy}
                  onValueChange={(value) => {
                    setSortBy(value as "newest" | "oldest" | "rating");
                    const eventName = getEventName("reviewsSortChanged");
                    AnalyticsService.trackGAEventHandler(eventName);
                    AnalyticsService.trackMx(eventName, { SortBy: value });
                  }}
                >
                  <DropdownMenuRadioItem value="newest">
                    {i18n.get("newest")}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="oldest">
                    {i18n.get("oldest")}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="rating">
                    {i18n.get("rating")}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* No reviews found Content */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        <div className="flex-1 px-4 pt-6 pb-64 min-h-0 overflow-y-auto flex flex-col">
          <div className="text-center py-12" role="status" aria-live="polite">
            <div className="flex flex-1 flex-col justify-center items-center px-4">
              <FolderDotted size={32} className="text-fg-neutral-strong" aria-hidden="true" />
              <p className="text-center title-s-semibold text-fg-neutral-strong mt-3">
                {i18n.get("reviews")}
              </p>
              <p className="text-center label-s-regular text-fg-neutral-subtle mt-2 max-w-sm">
                {i18n.get("noReviewsFound")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccessibilityReviewsTab;
